// src/pages/ClientDetail.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Divider,
  Grid,
  Avatar,
  Button,
  CircularProgress,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Chip,
  Stack,
  alpha,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import EventRepeatRoundedIcon from "@mui/icons-material/EventRepeatRounded";
import ShoppingBagRoundedIcon from "@mui/icons-material/ShoppingBagRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import HourglassEmptyRoundedIcon from "@mui/icons-material/HourglassEmptyRounded";
import { doc, getDoc, collection, query, where, onSnapshot } from "firebase/firestore";
import { db } from "../contexts/firebase/config";

function safe(v, fallback = "—") {
  return v ?? fallback;
}

function initials(name) {
  if (!name) return "C";
  const parts = String(name).trim().split(" ");
  const a = parts[0]?.[0] || "C";
  const b = parts[1]?.[0] || "";
  return (a + b).toUpperCase();
}

const currency = (n) => (typeof n === "number" ? `€ ${n.toFixed(2)}` : "—");

export default function ClientDetail() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const colors = {
    accent: theme.palette.primary.main,
    surface: isDark ? "rgba(15,23,42,0.78)" : "rgba(255,255,255,0.78)",
    card: isDark ? "rgba(15,23,42,0.84)" : "rgba(255,255,255,0.84)",
    cardStrong: isDark ? "rgba(15,23,42,0.96)" : "rgba(255,255,255,0.96)",
    border: isDark ? "rgba(148,163,184,0.16)" : "rgba(15,23,42,0.08)",
    text: theme.palette.text.primary,
    muted: theme.palette.text.secondary,
    hero: isDark ? "rgba(2,6,23,0.96)" : "rgba(15,23,42,0.96)",
  };

  const { clientId } = useParams();
  const [cliente, setCliente] = useState(null);
  const [loadingCliente, setLoadingCliente] = useState(true);
  const [compras, setCompras] = useState([]);
  const [loadingCompras, setLoadingCompras] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const snap = await getDoc(doc(db, "clientes", clientId));
        if (active) {
          if (snap.exists()) setCliente({ id: snap.id, ...snap.data() });
        }
      } finally {
        if (active) setLoadingCliente(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [clientId]);

  useEffect(() => {
    const q = query(collection(db, "compras"), where("clienteId", "==", clientId));
    const unsub = onSnapshot(q, (snap) => {
      const arr = snap.docs.map((d) => {
        const data = d.data();
        const dt = data.dataCompra?.toDate?.();
        return {
          id: d.id,
          ano: dt ? dt.getFullYear() : "—",
          valor: data.valor ?? 0,
          status: data.status || "—",
        };
      });
      setCompras(arr);
      setLoadingCompras(false);
    });
    return () => unsub();
  }, [clientId]);

  const totalGasto = useMemo(
    () => compras.reduce((acc, c) => acc + (typeof c.valor === "number" ? c.valor : 0), 0),
    [compras]
  );

  const comprasPagas = useMemo(
    () => compras.filter((c) => c.status === "Pago").length,
    [compras]
  );

  const avatarLabel = initials(cliente?.nome);
  const renovado = cliente?.estado === "Renovação";
  const dataRegisto = cliente?.dataRegisto?.toDate?.()?.toLocaleDateString?.() ?? "—";
  const dataFim = cliente?.dataFim?.toDate?.()?.toLocaleDateString?.() ?? "—";

  const InfoCard = ({ icon, label, value }) => (
    <Paper
      elevation={0}
      sx={{
        p: 2.2,
        height: "100%",
        borderRadius: "26px",
        border: `1px solid ${colors.border}`,
        bgcolor: colors.card,
        boxShadow: isDark
          ? "0 18px 42px rgba(0,0,0,0.22)"
          : "0 16px 38px rgba(15,23,42,0.06)",
      }}
    >
      <Stack direction="row" spacing={1.3} alignItems="center">
        <Avatar
          sx={{
            width: 42,
            height: 42,
            borderRadius: "16px",
            bgcolor: alpha(colors.accent, isDark ? 0.18 : 0.1),
            color: colors.accent,
          }}
        >
          {icon}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 800 }}>
            {label}
          </Typography>
          <Typography noWrap fontWeight={950} sx={{ color: colors.text }}>
            {value}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );

  if (loadingCliente) {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!cliente) {
    return (
      <Box sx={{ minHeight: "100vh", display: "grid", placeItems: "center", px: 2 }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: "30px",
            textAlign: "center",
            border: `1px solid ${colors.border}`,
            bgcolor: colors.card,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 950 }}>
            Cliente não encontrado.
          </Typography>
          <Button
            component={RouterLink}
            to="/dashboard/empresa/clientes"
            startIcon={<ArrowBackIcon />}
            sx={{ mt: 2, borderRadius: "999px", textTransform: "none", fontWeight: 900 }}
          >
            Voltar
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100%",
        position: "relative",
        overflow: "hidden",
        px: { xs: 1.25, sm: 2.5, md: 3.5 },
        py: { xs: 2, md: 3.5 },
        background: isDark
          ? "linear-gradient(135deg, #020617 0%, #0f172a 52%, #111827 100%)"
          : "linear-gradient(135deg, #f8fafc 0%, #eef2ff 48%, #f8fafc 100%)",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background: isDark
            ? `radial-gradient(900px 460px at 10% 5%, ${alpha(colors.accent, 0.18)}, transparent 62%), radial-gradient(760px 440px at 92% 12%, ${alpha(colors.accent, 0.12)}, transparent 58%)`
            : `radial-gradient(900px 460px at 10% 5%, ${alpha(colors.accent, 0.16)}, transparent 62%), radial-gradient(760px 440px at 92% 12%, ${alpha(colors.accent, 0.1)}, transparent 58%)`,
        }}
      />

      <Box
        sx={{
          position: "absolute",
          inset: 0,
          opacity: isDark ? 0.16 : 0.3,
          pointerEvents: "none",
          backgroundImage:
            "linear-gradient(rgba(148,163,184,0.16) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.16) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
          maskImage: "linear-gradient(to bottom, black, transparent 76%)",
        }}
      />

      <Box sx={{ position: "relative", zIndex: 1, width: "100%" }}>
        <Paper
          elevation={0}
          sx={{
            width: "100%",
            borderRadius: { xs: "28px", md: "42px" },
            border: `1px solid ${colors.border}`,
            bgcolor: colors.surface,
            backdropFilter: "blur(22px)",
            WebkitBackdropFilter: "blur(22px)",
            boxShadow: isDark
              ? "0 28px 90px rgba(0,0,0,0.36)"
              : "0 24px 80px rgba(15,23,42,0.10)",
            p: { xs: 2, sm: 3, md: 4 },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              position: "relative",
              overflow: "hidden",
              width: "100%",
              mb: 3.5,
              p: { xs: 2.5, md: 4 },
              borderRadius: "34px",
              bgcolor: colors.hero,
              color: "common.white",
              boxShadow: isDark
                ? "0 24px 70px rgba(0,0,0,0.34)"
                : "0 24px 70px rgba(15,23,42,0.22)",
              "&:before": {
                content: '""',
                position: "absolute",
                width: 560,
                height: 560,
                right: -150,
                top: -220,
                borderRadius: "50%",
                background: `radial-gradient(circle, ${alpha(colors.accent, 0.46)} 0%, ${alpha(colors.accent, 0.18)} 42%, transparent 70%)`,
              },
              "&:after": {
                content: '""',
                position: "absolute",
                width: 360,
                height: 360,
                left: "38%",
                bottom: -230,
                borderRadius: "50%",
                background: `radial-gradient(circle, ${alpha(colors.accent, 0.2)} 0%, transparent 68%)`,
              },
            }}
          >
            <Stack
              direction={{ xs: "column", lg: "row" }}
              spacing={3}
              justifyContent="space-between"
              alignItems="stretch"
              sx={{ position: "relative", zIndex: 1 }}
            >
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2.2} alignItems={{ xs: "flex-start", sm: "center" }}>
                <Avatar
                  sx={{
                    width: 92,
                    height: 92,
                    borderRadius: "30px",
                    fontSize: 34,
                    fontWeight: 950,
                    color: "#ffffff",
                    bgcolor: alpha(colors.accent, 0.28),
                    border: "1px solid rgba(255,255,255,0.18)",
                    boxShadow: "0 18px 44px rgba(0,0,0,0.18)",
                  }}
                >
                  {avatarLabel}
                </Avatar>

                <Box>
                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1.4 }}>
                    <Chip
                      label={renovado ? "Renovado" : safe(cliente.estado, "Novo")}
                      size="small"
                      sx={{
                        borderRadius: "999px",
                        fontWeight: 900,
                        bgcolor: renovado
                          ? alpha(theme.palette.success.main, 0.22)
                          : alpha(colors.accent, 0.24),
                        color: "#ffffff",
                        border: "1px solid rgba(255,255,255,0.18)",
                      }}
                    />
                    <Chip
                      label={`Registo: ${dataRegisto}`}
                      size="small"
                      sx={{
                        borderRadius: "999px",
                        fontWeight: 900,
                        bgcolor: "rgba(255,255,255,0.12)",
                        color: "#ffffff",
                        border: "1px solid rgba(255,255,255,0.18)",
                      }}
                    />
                  </Stack>

                  <Typography
                    sx={{
                      fontSize: { xs: "2.15rem", sm: "2.85rem", md: "3.7rem" },
                      fontWeight: 950,
                      lineHeight: 0.98,
                      letterSpacing: "-0.07em",
                      color: "#ffffff",
                    }}
                  >
                    {safe(cliente.nome)}
                  </Typography>

                  <Typography
                    sx={{
                      mt: 1.4,
                      color: "rgba(255,255,255,0.68)",
                      fontSize: { xs: "0.98rem", md: "1.08rem" },
                      lineHeight: 1.75,
                    }}
                  >
                    Detalhes do cliente, plano associado, renovação e histórico de compras registadas.
                  </Typography>
                </Box>
              </Stack>

              <Stack direction={{ xs: "row", lg: "column" }} spacing={1.25} useFlexGap flexWrap="wrap" sx={{ width: { xs: "100%", lg: 260 }, justifyContent: "center" }}>
                <Paper
                  elevation={0}
                  sx={{
                    px: 1.6,
                    py: 1.25,
                    borderRadius: "26px",
                    bgcolor: alpha(theme.palette.background.paper, isDark ? 0.14 : 0.74),
                    border: "1px solid rgba(255,255,255,0.18)",
                    minWidth: { xs: "100%", sm: 230 },
                  }}
                >
                  <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.66)", fontWeight: 800 }}>
                    Total gasto
                  </Typography>
                  <Typography sx={{ fontSize: "1.18rem", fontWeight: 950, color: "#ffffff" }}>
                    {currency(totalGasto)}
                  </Typography>
                </Paper>
                <Paper
                  elevation={0}
                  sx={{
                    px: 1.6,
                    py: 1.25,
                    borderRadius: "26px",
                    bgcolor: alpha(theme.palette.background.paper, isDark ? 0.14 : 0.74),
                    border: "1px solid rgba(255,255,255,0.18)",
                    minWidth: { xs: "100%", sm: 230 },
                  }}
                >
                  <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.66)", fontWeight: 800 }}>
                    Compras
                  </Typography>
                  <Typography sx={{ fontSize: "1.18rem", fontWeight: 950, color: "#ffffff" }}>
                    {compras.length} registo(s)
                  </Typography>
                </Paper>
              </Stack>
            </Stack>
          </Paper>

          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 3 }}>
            <Button
              component={RouterLink}
              to="/dashboard/empresa/clientes"
              startIcon={<ArrowBackIcon />}
              sx={{
                height: 40,
                px: 2,
                borderRadius: "999px",
                fontWeight: 900,
                textTransform: "none",
                color: colors.text,
                bgcolor: colors.card,
                border: `1px solid ${colors.border}`,
                "&:hover": { bgcolor: colors.cardStrong },
              }}
            >
              Voltar
            </Button>
          </Stack>

          <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
            <Grid item xs={12} md={4}>
              <InfoCard icon={<EmailRoundedIcon />} label="E-mail" value={safe(cliente.email)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <InfoCard icon={<WorkspacePremiumRoundedIcon />} label="Plano" value={safe(cliente.plano)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <InfoCard icon={<EventRepeatRoundedIcon />} label="Próxima Renovação" value={dataFim} />
            </Grid>
          </Grid>

          <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
            <Grid item xs={12} md={4}>
              <InfoCard icon={<ShoppingBagRoundedIcon />} label="Compras" value={compras.length} />
            </Grid>
            <Grid item xs={12} md={4}>
              <InfoCard icon={<PaymentsRoundedIcon />} label="Total gasto" value={currency(totalGasto)} />
            </Grid>
            <Grid item xs={12} md={4}>
              <InfoCard icon={<CheckCircleRoundedIcon />} label="Compras pagas" value={comprasPagas} />
            </Grid>
          </Grid>

          <Paper
            elevation={0}
            sx={{
              borderRadius: "30px",
              border: `1px solid ${colors.border}`,
              bgcolor: colors.card,
              backdropFilter: "blur(16px)",
              WebkitBackdropFilter: "blur(16px)",
              boxShadow: isDark
                ? "0 24px 64px rgba(0,0,0,0.28)"
                : "0 24px 60px rgba(15,23,42,0.07)",
              overflow: "hidden",
            }}
          >
            <Box sx={{ px: { xs: 2.25, md: 3 }, py: 2.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                <Stack direction="row" spacing={1.45} alignItems="center" sx={{ minWidth: 0 }}>
                  <Avatar
                    sx={{
                      width: 46,
                      height: 46,
                      borderRadius: "17px",
                      bgcolor: alpha(colors.accent, isDark ? 0.18 : 0.1),
                      color: colors.accent,
                    }}
                  >
                    <ShoppingBagRoundedIcon />
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="h6" fontWeight={950} sx={{ color: colors.text }}>
                      Compras
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                      Histórico de compras associado a este cliente.
                    </Typography>
                  </Box>
                </Stack>

                <Chip
                  label={`${compras.length} registo(s)`}
                  sx={{
                    flexShrink: 0,
                    borderRadius: "999px",
                    fontWeight: 900,
                    bgcolor: alpha(colors.accent, isDark ? 0.16 : 0.08),
                    color: colors.accent,
                  }}
                />
              </Stack>
            </Box>

            <Divider sx={{ borderColor: colors.border }} />

            {loadingCompras ? (
              <Box sx={{ textAlign: "center", py: 5 }}>
                <CircularProgress />
              </Box>
            ) : compras.length === 0 ? (
              <Box sx={{ py: 5, textAlign: "center", px: 2 }}>
                <Avatar
                  sx={{
                    width: 64,
                    height: 64,
                    mx: "auto",
                    mb: 2,
                    borderRadius: "24px",
                    bgcolor: alpha(colors.accent, isDark ? 0.18 : 0.1),
                    color: colors.accent,
                  }}
                >
                  <ShoppingBagRoundedIcon />
                </Avatar>
                <Typography sx={{ fontWeight: 950, color: colors.text }}>
                  Este cliente ainda não tem compras registadas.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Quando existirem compras, elas aparecem nesta tabela.
                </Typography>
              </Box>
            ) : (
              <TableContainer component={Box} sx={{ bgcolor: "transparent" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow
                      sx={{
                        "& th": {
                          bgcolor: isDark ? "rgba(2,6,23,0.38)" : "rgba(248,250,252,0.92)",
                          fontWeight: 900,
                          color: colors.muted,
                          borderBottom: `1px solid ${colors.border}`,
                          fontSize: "0.76rem",
                          textTransform: "uppercase",
                          letterSpacing: "0.08em",
                          py: 1.45,
                        },
                      }}
                    >
                      <TableCell>Ano da Compra</TableCell>
                      <TableCell align="right">Valor (€)</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {compras.map((c) => {
                      const pago = c.status === "Pago";
                      return (
                        <TableRow
                          key={c.id}
                          hover
                          sx={{
                            transition: "all .18s ease",
                            "& td": {
                              borderBottom: `1px solid ${colors.border}`,
                              py: 1.7,
                            },
                            "&:last-child td": { borderBottom: 0 },
                            "&:hover": {
                              bgcolor: alpha(colors.accent, isDark ? 0.08 : 0.045),
                            },
                          }}
                        >
                          <TableCell>
                            <Stack direction="row" spacing={1.2} alignItems="center">
                              <Avatar
                                sx={{
                                  width: 34,
                                  height: 34,
                                  borderRadius: "14px",
                                  bgcolor: alpha(colors.accent, isDark ? 0.18 : 0.1),
                                  color: colors.accent,
                                }}
                              >
                                <CalendarMonthRoundedIcon fontSize="small" />
                              </Avatar>
                              <Typography sx={{ fontWeight: 850, color: colors.text }}>
                                {c.ano}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              size="small"
                              label={currency(c.valor)}
                              sx={{
                                borderRadius: "999px",
                                fontWeight: 900,
                                bgcolor: alpha(colors.accent, isDark ? 0.16 : 0.08),
                                color: colors.accent,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              icon={pago ? <CheckCircleRoundedIcon /> : <HourglassEmptyRoundedIcon />}
                              label={c.status}
                              sx={{
                                borderRadius: "999px",
                                fontWeight: 900,
                                bgcolor: pago
                                  ? alpha(theme.palette.success.main, isDark ? 0.18 : 0.1)
                                  : alpha(theme.palette.warning.main, isDark ? 0.2 : 0.12),
                                color: pago ? theme.palette.success.main : theme.palette.warning.main,
                                "& .MuiChip-icon": {
                                  color: "inherit",
                                },
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Paper>
      </Box>
    </Box>
  );
}
